/**
 * Frontend hooks for the CoLoop integration. Talks to the dashboard
 * endpoints in `apps/dashboard/src/integrations/coloop/coloop.controller.ts`.
 *
 * Token storage / refresh / encryption all happen server-side; the frontend
 * never touches credentials.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StaleTime, QueryTag } from '@common/ts/enums';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstance from '@/axios/axiosInstance';
import { API_ENDPOINTS } from '@/api-endpoints';

export type IntegrationProvider = 'coloop';
export type IntegrationStatus = 'connected' | 'expired' | 'revoked';

export interface OrganizationIntegration {
  provider: IntegrationProvider;
  status: IntegrationStatus;
  metadata: {
    connectedEmail?: string;
    scopes?: string[];
    [key: string]: unknown;
  };
  lastVerifiedAt: string | null;
  /**
   * ObjectId of the user who FIRST connected the integration. Kept
   * pinned even after a revoke → reconnect by a different admin.
   */
  createdBy: string;
  createdByName: string | null;
  /**
   * ObjectId of the user whose OAuth session issued the CURRENTLY
   * stored tokens. Differs from `createdBy` when a different admin
   * reconnected after a revoke — that admin's CoLoop identity is
   * now on the hook for downstream pushes.
   */
  lastConnectedBy: string | null;
  lastConnectedByName: string | null;
  lastConnectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Backwards-compatible alias — some callsites already reference this. */
export type IntegrationRecord = OrganizationIntegration;

export interface ColoopWorkspace {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface ColoopProject {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface ProjectIntegration {
  provider: IntegrationProvider;
  remote: {
    workspaceId: string;
    workspaceName?: string;
    projectId: string;
    projectName?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// ---- Org-level ----

export function useGetOrgIntegrations(organizationId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.INTEGRATIONS(organizationId),
    queryFn: async (): Promise<OrganizationIntegration[]> => {
      const { data } = await axiosInstance.get(API_ENDPOINTS.INTEGRATIONS.LIST(organizationId));
      return data;
    },
    enabled: Boolean(organizationId),
    staleTime: StaleTime.SHORT,
  });
}

export function useStartColoopAuthorize(organizationId?: string) {
  return useMutation({
    mutationFn: async (args?: { redirectTarget?: string }) => {
      const { data } = await axiosInstance.get<{ authorizeUrl: string }>(
        API_ENDPOINTS.INTEGRATIONS.COLOOP_AUTHORIZE(organizationId),
        { params: args?.redirectTarget ? { redirectTarget: args.redirectTarget } : undefined },
      );
      return data;
    },
  });
}

export function useDisconnectColoop(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(API_ENDPOINTS.INTEGRATIONS.COLOOP_DISCONNECT(organizationId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATIONS(organizationId) });
      // Any open project page has stale PROJECT_INTEGRATION data. Prefix
      // match without projectId invalidates every project's mapping so
      // the linked-view immediately reverts to the "not connected" copy.
      queryClient.invalidateQueries({ queryKey: [QueryTag.PROJECT_INTEGRATION] });
    },
  });
}

export function useGetColoopWorkspaces(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.COLOOP_WORKSPACES(organizationId),
    queryFn: async (): Promise<ColoopWorkspace[]> => {
      const { data } = await axiosInstance.get(
        API_ENDPOINTS.INTEGRATIONS.COLOOP_WORKSPACES(organizationId),
      );
      return data;
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: StaleTime.MEDIUM,
  });
}

export function useGetColoopWorkspaceProjects(
  organizationId?: string,
  workspaceId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.COLOOP_WORKSPACE_PROJECTS(organizationId, workspaceId),
    queryFn: async (): Promise<ColoopProject[]> => {
      const { data } = await axiosInstance.get(
        API_ENDPOINTS.INTEGRATIONS.COLOOP_WORKSPACE_PROJECTS(organizationId, workspaceId),
      );
      return data;
    },
    enabled: enabled && Boolean(organizationId) && Boolean(workspaceId),
    staleTime: StaleTime.MEDIUM,
  });
}

// ---- Per-Tellet-project ↔ CoLoop-project mapping ----

export function useGetProjectIntegration(projectId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_INTEGRATION(projectId),
    queryFn: async (): Promise<ProjectIntegration | null> => {
      const { data } = await axiosInstance.get(
        API_ENDPOINTS.INTEGRATIONS.PROJECT_MAPPING(projectId),
      );
      return data ?? null;
    },
    enabled: Boolean(projectId),
    staleTime: StaleTime.MEDIUM,
  });
}

export interface SetProjectMappingPayload {
  organizationId: string;
  workspaceId: string;
  workspaceName?: string;
  projectId: string;
  projectName?: string;
}

export function useSetProjectMapping(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SetProjectMappingPayload) => {
      const { data } = await axiosInstance.put(
        API_ENDPOINTS.INTEGRATIONS.PROJECT_MAPPING(projectId),
        payload,
      );
      return data as ProjectIntegration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_INTEGRATION(projectId) });
      // Also invalidate the CoLoop-side project list so re-opening the
      // picker after Save/Unlink doesn't show a stale "already linked"
      // flag from CoLoop's response.
      queryClient.invalidateQueries({ queryKey: [QueryTag.COLOOP_WORKSPACE_PROJECTS] });
      // Export rows are scoped to the linked CoLoop project; without this the
      // panel briefly shows the previous project's rows after a re-link.
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_EXPORTS(projectId) });
    },
  });
}

export function useClearProjectMapping(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(API_ENDPOINTS.INTEGRATIONS.PROJECT_MAPPING(projectId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_INTEGRATION(projectId) });
      queryClient.invalidateQueries({ queryKey: [QueryTag.COLOOP_WORKSPACE_PROJECTS] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_EXPORTS(projectId) });
    },
  });
}

// ---- Push pipeline ----

export type ConversationExportStatus = 'pending' | 'in_flight' | 'succeeded' | 'failed' | 'skipped';

export interface ConversationExport {
  _id: string;
  conversationId: string;
  status: ConversationExportStatus;
  externalId: string;
  remote?: {
    transcriptId?: string;
    deepLink?: string;
    workspaceId?: string;
    remoteProjectId?: string;
  };
  attempts: number;
  enqueuedAt: string;
  finishedAt?: string | null;
  lastError?: { message?: string } | null;
}

export interface PushResult {
  attemptGroupId?: string;
  enqueuedCount: number;
  chunkCount?: number;
  reason?: string;
  /** Skipped because the conversation already has a succeeded export. */
  skippedSucceededCount?: number;
  /** Skipped because a prior push is still being processed by the worker. */
  skippedInFlightCount?: number;
}

export function usePushConversations(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // organizationId is no longer sent from the client — the backend
    // derives it from the project's workspace, closing the cross-org
    // token exfiltration hole where a client could push against any
    // projectId with any orgId.
    mutationFn: async (payload: { conversationIds: string[] }) => {
      const { data } = await axiosInstance.post<PushResult>(
        API_ENDPOINTS.INTEGRATIONS.PUSH(projectId),
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_EXPORTS(projectId) });
    },
  });
}

export function usePushAllConversations(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post<PushResult>(
        API_ENDPOINTS.INTEGRATIONS.PUSH_ALL(projectId),
        {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_EXPORTS(projectId) });
    },
  });
}

/**
 * Poll only while something is actually in flight — otherwise every
 * project page with a mapping would hit the exports endpoint every 4s
 * forever, creating background load with no user benefit. Once the
 * queue drains, polling stops until the next Push click.
 */
function pickPollInterval(data: ConversationExport[] | undefined): number | false {
  if (!data) return false;
  return data.some((e) => e.status === 'pending' || e.status === 'in_flight') ? 4000 : false;
}

export function useGetProjectExports(projectId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_EXPORTS(projectId),
    queryFn: async (): Promise<ConversationExport[]> => {
      const { data } = await axiosInstance.get(API_ENDPOINTS.INTEGRATIONS.EXPORTS(projectId));
      return data;
    },
    enabled: Boolean(projectId),
    refetchInterval: (query) =>
      pickPollInterval(query.state.data as ConversationExport[] | undefined),
    // Keep polling even when the window is not focused. Without this,
    // TanStack gates the interval's fetch behind `focusManager.isFocused()`
    // (queryObserver: `refetchIntervalInBackground || isFocused()`), so the
    // moment the researcher tabs away to CoLoop/email while a push is
    // processing the status table silently freezes on "in flight" and only
    // catches up on refocus. The interval already self-terminates once no
    // row is pending/in_flight, so this stays a short, push-scoped poll —
    // never a permanent background drain.
    refetchIntervalInBackground: true,
    staleTime: StaleTime.IMMEDIATE,
  });
}
